import React from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ShieldCheck, Droplets, Home, Star, Phone, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* ── Hero / Mission ─────────────────────────────────────────────── */}
      <section className="py-16 bg-slate-50 border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <p className="text-sm font-semibold uppercase tracking-widest mb-3" style={{ color: '#1571BA' }}>
            About Aquify
          </p>
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6 leading-tight">
            Better Water for Every Home.{' '}
            <span style={{ color: '#1571BA' }}>Better Life for Every Family.</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Aquify was founded on a straightforward idea: every family deserves clean, healthy water —
            and getting there should be simple, honest, and done right the first time.
          </p>
        </div>
      </section>

      {/* ── About Us ─────────────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          
          {/* Company Overview */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">Aquify Filtration Partners LLC</h2>
            <p className="text-lg text-gray-600 leading-relaxed">
              Aquify Filtration Partners LLC (Aquify) is an environmentally responsible water filtration design, sales, installation, and service company. Aquify provides water filtration systems to residential and commercial clients in Nassau and Suffolk Counties, the Greater New York City Area, and Palm Beach and Broward Counties, Florida.
            </p>
          </div>

          {/* Mission & Vision Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-20">
            {/* Mission Card */}
            <div className="rounded-2xl p-8 shadow-md border border-gray-100" style={{ backgroundColor: '#F8FAFC' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0C4288' }}>Our Mission</h3>
              <p className="text-gray-700 leading-relaxed">
                Aquify’s mission is to improve water quality and sustainability through science-driven technology, expert installation, and exceptional customer service.
              </p>
            </div>
            
            {/* Vision Card */}
            <div className="rounded-2xl p-8 shadow-md border border-gray-100" style={{ backgroundColor: '#F8FAFC' }}>
              <h3 className="text-2xl font-bold mb-4" style={{ color: '#0C4288' }}>Our Vision</h3>
              <p className="text-gray-700 leading-relaxed">
                Aquify’s vision is to become Long Island’s most trusted name in water purification, recognized for integrity, expertise, and customer care.
              </p>
            </div>
          </div>

          {/* Leadership Section */}
          <div className="mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-10 text-center">About Us</h2>
            
            <div className="grid md:grid-cols-2 gap-12">
              {/* Daryl Dworkin Profile */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">Daryl Dworkin, MWS</h3>
                <p className="text-md font-semibold mb-6" style={{ color: '#1571BA' }}>Founder & Principal</p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Daryl Dworkin brings more than a decade of hands-on experience in the water treatment industry, with expertise spanning water testing and analysis, system design, and the sales, installation, and service of point-of-entry and point-of-use filtration systems — including specialized work in reverse osmosis and advanced purification technology.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Daryl holds the prestigious Master Water Specialist (MWS) designation from the Water Quality Association, the industry’s highest professional certification, along with a Master of Science in Environmental Science from the University of Maryland. As founder and principal of Aquify Filtration Partners, he draws on this technical and academic foundation to deliver precise, reliable water quality services across residential, commercial, and institutional settings — including complex sampling and analysis programs for healthcare environments, where accuracy and compliance are essential.
                </p>
              </div>
              
              {/* Jack Senzon Profile */}
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1">Jack Senzon</h3>
                <p className="text-md font-semibold mb-6" style={{ color: '#1571BA' }}>Partner & National Sales Director</p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Jack Senzon brings nine years of hands-on expertise in residential and commercial water filtration, specializing in whole-house filtration, water softening, and high-performance reverse osmosis drinking systems. He has helped thousands of homeowners and businesses achieve cleaner, safer, better-tasting water through customized, long-term solutions tailored to each property’s needs.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Jack takes a consultative approach, focusing first on understanding each customer’s needs before recommending the right solution from a range of options. Before water filtration, he spent two decades in finance, which shaped the disciplined, analytical approach he now brings to diagnosing water issues and educating customers with clarity.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  His mission is simple: to help every customer make confident, informed decisions that improve the health, comfort, and value of their home or business, without pressure. Jack takes pride not just in the systems he installs, but in the trust he builds — treating every customer like family.
                </p>
              </div>
            </div>
          </div>

          {/* Closing Partnership Statement */}
          <div 
            className="rounded-2xl p-8 shadow-xl text-center"
            style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}
          >
            <p className="text-white text-xl font-semibold leading-relaxed">
              Daryl and Jack have been close friends for more than 45 years — a partnership built on the same trust and integrity they bring to every customer relationship.
            </p>
          </div>
          
        </div>
      </section>

      {/* ── What We Do ───────────────────────────────────────────────────── */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">What Aquify Does</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We provide full-service water treatment — from evaluation through installation and beyond.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Water Evaluations */}
            <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: '#E3F2FD' }}>
                <Droplets className="h-7 w-7" style={{ color: '#1571BA' }} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Professional Water Evaluations</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Every Aquify engagement begins with a complimentary, no-obligation water evaluation.
                We visit your home, test your water for hardness, iron, chlorine, pH, TDS, and other
                key indicators — then walk you through exactly what we found and what it means for your family.
              </p>
              <p className="text-gray-600 leading-relaxed">
                No pressure. No sales tactics. Just honest answers about your water.
              </p>
            </div>

            {/* Whole-House Filtration */}
            <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: '#E3F2FD' }}>
                <Home className="h-7 w-7" style={{ color: '#1571BA' }} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Whole-House Filtration</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Our flagship service. A whole-house filtration system treats your water at the point
                it enters your home — so every tap, every shower, and every appliance delivers clean,
                filtered water. Custom-designed for your specific water chemistry, your home's size, and
                your household's needs.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Protection that works quietly in the background — every single day.
              </p>
            </div>

            {/* Certified Expertise */}
            <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: '#E3F2FD' }}>
                <ShieldCheck className="h-7 w-7" style={{ color: '#1571BA' }} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Master Water Specialist</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Aquify's water treatment systems are designed by a Master Water Specialist — a
                Master Water Specialist (MWS) credentialed through the Water Quality Association (WQA),
                the gold standard in the industry.
              </p>
              <p className="text-gray-600 leading-relaxed">
                That means your system isn't just installed — it's engineered. Built to solve the
                specific problems in your water, not just the easiest ones to sell.
              </p>
            </div>

            {/* Qualified Plumber Installation */}
            <div className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 hover:shadow-lg transition-shadow duration-300">
              <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-5" style={{ backgroundColor: '#E3F2FD' }}>
                <Star className="h-7 w-7" style={{ color: '#1571BA' }} />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-3">Qualified Plumber Installation</h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Every Aquify system is installed by a qualified plumber. That's not standard in this
                industry — and it's one of the reasons homeowners choose us. Proper installation means
                proper performance, proper warranties, and no code issues down the road.
              </p>
              <p className="text-gray-600 leading-relaxed">
                When we leave your home, everything works — and works correctly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Why It Matters ───────────────────────────────────────────────── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Why Water Quality Matters
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              The water coming into your home affects more than what you drink.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                heading: 'Health & Comfort',
                body: 'Chlorine, heavy metals, and hard water minerals affect how your skin feels, how your water tastes, and what you\'re actually putting into your body every day.',
              },
              {
                heading: 'Home Protection',
                body: 'Hard water and acidic water silently damage pipes, water heaters, dishwashers, and fixtures — costing homeowners thousands in premature repairs and replacements.',
              },
              {
                heading: 'Residential Quality of Life',
                body: 'Soft, filtered water means cleaner dishes, softer laundry, better-tasting coffee, and fewer mineral deposits on your surfaces. Small upgrades. Big difference.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="rounded-xl p-6 border-l-4"
                style={{ borderColor: '#1571BA', backgroundColor: '#F0F7FF' }}
              >
                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.heading}</h3>
                <p className="text-gray-600 leading-relaxed">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Service Areas ─────────────────────────────────────────────────── */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-5xl mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-slate-900 mb-4">Where We Serve</h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Aquify serves residential and light commercial customers across Long Island, New York, and South Florida.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              'Nassau County', 'Suffolk County', 'New York City', 'Westchester',
              'Palm Beach County', 'Broward County',
            ].map((area) => (
              <span
                key={area}
                className="px-5 py-2 rounded-full text-sm font-semibold"
                style={{ backgroundColor: '#E3F2FD', color: '#0C4288', border: '1px solid #BFDBFE' }}
              >
                {area}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────────────────── */}
      <section
        className="py-20"
        style={{ background: 'linear-gradient(135deg, #0C4288 0%, #1571BA 100%)' }}
      >
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-5">
            Ready to Find Out What's in Your Water?
          </h2>
          <p className="text-[#E3F2FD] text-lg mb-8 leading-relaxed">
            Schedule your complimentary water evaluation today — no cost, no obligation, and no
            pressure. Just an honest conversation about your water.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://app.kickserv.com/9db783/self_service/requests/new"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-white font-bold text-lg px-10 py-4 rounded-xl transition-all duration-300 hover:scale-105 shadow-lg"
              style={{ color: '#0C4288' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#E3F2FD'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'white'; }}
            >
              Request a Complimentary Evaluation
              <ArrowRight className="h-5 w-5" />
            </a>
            <a
              href="tel:5167299013"
              className="inline-flex items-center justify-center gap-2 font-bold text-lg px-10 py-4 rounded-xl transition-all duration-300 hover:scale-105"
              style={{ border: '2px solid rgba(255,255,255,0.6)', color: '#ffffff' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
            >
              <Phone className="h-5 w-5" />
              516-729-9013
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
